import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import logo from "../assets/logo.svg";
import logoDark from "../assets/logo-dark.svg";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";
import { logInfo, logError } from "../utils/logger";

const backendUrl = getBackendUrl();
const drawerWidth = 240;


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    overflow: "hidden",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main, // Usa cor do tema
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    },
  },

  chip: {
    background: "red",
    color: "white",
  },

  avatar: {
    width: "100%",
  },

  toolbar: {
    paddingRight: 8,
    paddingLeft: 8,
    background: theme.mode === "light" ? "#ffffff" : "#1e293b",
    color: theme.mode === "light" ? "#374151" : "#e2e8f0",
    borderBottom: `1px solid ${theme.mode === "light" ? "#e8edf3" : "#334155"}`,
    boxShadow: "none",
    transition: "all 0.3s ease",
    minHeight: "36px !important",
    overflow: "hidden",
    // Estiliza todos os IconButtons da toolbar de forma consistente
    "& .MuiIconButton-root": {
      borderRadius: "10px",
      padding: "7px",
      color: "inherit",
      transition: "background-color 0.2s ease, transform 0.15s ease",
      "&:hover": {
        backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
    },
    // Garante que ícones SVG dentro herdem a cor
    "& .MuiIconButton-root .MuiSvgIcon-root": {
      fontSize: "1.25rem",
    },
    // Badge não herda, mantém cor própria
    "& .MuiBadge-badge": {
      fontWeight: 700,
      fontSize: "0.65rem",
      minWidth: 16,
      height: 16,
      padding: "0 4px",
    },
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px 0 8px",
    minHeight: "36px",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      height: "36px",
    },
    backgroundColor: "transparent",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    transition: "all 0.3s ease",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  menuButtonHidden: {
    display: "none",
  },

  title: {
    flexGrow: 1,
    fontSize: 14,
    color: theme.mode === "light" ? "#374151" : "#e2e8f0",
    fontWeight: 600,
    letterSpacing: "0.025em",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 55%, #0f3460 100%)",
    borderRight: "none",
    boxShadow: "4px 0 24px rgba(0, 0, 0, 0.25)",
    color: "#e2e8f0",
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },

  appBarSpacer: {
    height: "36px",
    flexShrink: 0,
  },

  content: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
  },

  container: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    minHeight: 0,
    "&::-webkit-scrollbar": {
      width: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "scrollbar-width": "thin",
    "scrollbar-color": "rgba(255,255,255,0.2) transparent",
  },

  NotificationsPopOver: {
    // Mantém original
  },

  logo: {
    width: "100%",
    //height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease", // Transição suave
    "&:hover": {
      transform: "scale(1.02)", // Pequeno zoom no hover
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: 34,
    height: 34,
    cursor: "pointer",
    borderRadius: "50%",
    border: `2px solid ${theme.mode === "light" ? "#e2e8f0" : "#334155"}`,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.06)",
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}25`,
    },
  },

  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  toolbarButton: {
    color: theme.mode === "light" ? "#374151" : "#e2e8f0",
    borderRadius: "8px",
    padding: "8px",
    margin: "0 2px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  menuButton: {
    color: theme.mode === "light" ? "#374151" : "#e2e8f0",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
    },
    "& .MuiSvgIcon-root": {
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
      transform: "rotate(90deg)",
    },
  },

  languageSelector: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      background: "none",
      border: "none",
      borderRadius: "8px",
      color: theme.mode === "light" ? "#374151" : "#e2e8f0",
      fontSize: "18px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        background: theme.mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.08)",
      },
    },
    "& > div": {
      position: "absolute",
      top: "45px",
      left: "0",
      background: theme.mode === "light" ? "#ffffff" : "#1e293b",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      borderRadius: "10px",
      padding: "6px",
      zIndex: 1000,
      minWidth: "130px",
      border: `1px solid ${theme.mode === "light" ? "#e8edf3" : "#334155"}`,
      "& button": {
        background: "none",
        border: "none",
        color: theme.mode === "light" ? "#374151" : "#e2e8f0",
        display: "block",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "7px",
        fontSize: "13px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
          background: `${theme.palette.primary.main}15`,
          color: theme.palette.primary.main,
        },
      },
    },
  },

  // Separador vertical na toolbar
  toolbarDivider: {
    width: 1,
    height: 24,
    backgroundColor: theme.mode === "light" ? "#e2e8f0" : "#334155",
    margin: "0 4px",
  },

  // Badge animado
  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  const fetchAnnouncements = useCallback(async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncementsRaw = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        // Backend já filtra por empresa excluindo os reconhecidos
        const activeAnnouncements = activeAnnouncementsRaw;
        setAnnouncements(activeAnnouncements);
        setShowAnnouncementsModal(activeAnnouncements.length > 0);
      } catch (err) {
        toastError(err);
      }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id, fetchAnnouncements]);

  // Atualiza checkbox ao trocar de aviso
  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    // Não precisamos mais ler localStorage; tratamos via backend
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        // Remove este aviso da lista
        setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        // Opcional: recarregar lista para reexibir (se desejar permitir desfazer)
        await fetchAnnouncements();
      }
      // Fecha modal se não restarem avisos
      setShowAnnouncementsModal((prev) => {
        return announcements.length - 1 > 0;
      });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    

    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "closed") {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de usuário recebido", { data });
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          logInfo("Usuário faz aniversário hoje; exibindo modal");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          logInfo("Há aniversariantes hoje", { birthdayData });
        }
      } catch (error) {
        logError("Erro ao verificar aniversários", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img
              className={classes.logo}
              style={{
                display: drawerOpen ? "block" : "none",
                margin: "0 auto",
                width: "100%",
              }}
              alt="logo"
            />
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <List className={classes.containerWithScroll}>
            {/* {mainListItems} */}
            <MainListItems collapsed={!drawerOpen} />
          </List>
          <Divider style={{ background: "rgba(255, 255, 255, 0.08)" }} />
        </Drawer>
      )}

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, !hideMenu && drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="open drawer"
              style={{ color: theme.mode === "light" ? "#374151" : "#e2e8f0" }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {/* {greaterThenSm && user?.profile === "admin" && getDateAndDifDays(user?.company?.dueDate).difData < 7 ? ( */}
            {greaterThenSm &&
              user?.profile === "admin" &&
              user?.company?.dueDate ? (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>! (
                {i18n.t("mainDrawer.appBar.user.active")}{" "}
                {dateToClient(user?.company?.dueDate)})
              </>
            ) : (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>!
              </>
            )}
          </Typography>

          {!hideMenu && (
            <>
              <VersionControl
                onUpdateStart={handleUpdateStart}
                onUpdateComplete={handleUpdateComplete}
              />

              <div
                style={{ position: "relative", display: "inline-block" }}
                className="language-dropdown"
              >
                <button
                  onClick={() => setShowOptions(!showOptions)}
                  style={{
                    background: "none",
                    border: "none",
                    color: theme.mode === "light" ? "#374151" : "#e2e8f0",
                    cursor: "pointer",
                    fontSize: "20px",
                    paddingRight: "16px",
                    paddingTop: "8px",
                  }}
                >
                  <FaGlobe />
                </button>

                {showOptions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "35px",
                      left: "0",
                      background: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      borderRadius: "8px",
                      padding: "8px",
                      zIndex: 1000,
                      minWidth: "120px",
                      maxWidth: "200px",
                    }}
                  >
                    {filteredLanguageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                          padding: "4px",
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <IconButton onClick={colorMode.toggleColorMode}>
                {theme.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>

              <NotificationsVolume setVolume={setVolume} volume={volume} />

              <IconButton
                onClick={handleRefreshPage}
                aria-label={i18n.t("mainDrawer.appBar.refresh")}
                color="inherit"
              >
                <CachedIcon />
              </IconButton>

              {user.id && <NotificationsPopOver volume={volume} />}

              <AnnouncementsPopover />

              <ChatPopover />

              {/* Separador visual antes do avatar */}
              <div className={classes.toolbarDivider} />

              <div className="user-menu-wrapper">
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  variant="dot"
                  onClick={handleMenu}
                  style={{ cursor: "pointer" }}
                >
                  <Avatar
                    alt="Quero Atendimento"
                    className={classes.avatar2}
                    src={profileUrl}
                  />
                </StyledBadge>

                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  userId={user?.id}
                />

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  PaperProps={{
                    style: {
                      minWidth: "150px",
                      maxWidth: "200px",
                      width: "auto",
                    },
                  }}
                >
                  <MenuItem onClick={handleOpenUserModal}>
                    {i18n.t("mainDrawer.appBar.user.profile")}
                  </MenuItem>
                  <MenuItem onClick={handleClickLogout}>
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={ackChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAckChecked(checked);
                      handleToggleAcknowledge(selectedAnnouncement.id, checked);
                    }}
                  />
                }
                label="Estou ciente e não mostrar novamente"
              />
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

    </div>
  );
};

export default LoggedInLayout;
